<template>
  <div class="main-layout">
    <!-- 顶部头部：团队 | Logo + 系统名 | 用户 -->
    <div class="top-header">
      <div class="header-left">
        <span class="header-team">山东大学 · 行星科学团队</span>
      </div>

      <div class="header-center">
        <img src="../../assets/xiaohui.png" alt="校徽" class="header-logo" />
        <span class="header-title">HFLIBS可视化分析系统</span>
      </div>

      <div class="header-right">
        <span class="header-datetime">{{ dateTimeText }}</span>
        <el-dropdown trigger="hover" @command="handleCommand" class="user-dropdown">
          <span class="user-trigger">
            <i class="el-icon-user-solid"></i>
            {{ userInfo.userName || defaultName }}
            <i class="el-icon-arrow-down"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item disabled>
              <span style="color: var(--text-muted);">{{ userInfo.role || roleLabel }}</span>
            </el-dropdown-item>
            <el-dropdown-item divided command="personal">
              <i class="el-icon-user"></i> 个人中心
            </el-dropdown-item>
            <el-dropdown-item command="changePassword">
              <i class="el-icon-lock"></i> 修改密码
            </el-dropdown-item>
            <el-dropdown-item divided command="switchAccount">
              <i class="el-icon-switch-button"></i> 退出
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <!-- 底部导航栏 -->
    <div class="top-nav">
      <div class="nav-tabs">
        <router-link
          v-for="item in menuItems"
          :key="item.path"
          :to="item.path"
          class="nav-tab"
          :class="{ active: activeMenu === item.path }"
        >
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
        </router-link>
      </div>
    </div>

    <!-- 页面内容 -->
    <div class="page-content">
      <router-view />
    </div>

    <!-- 修改密码对话框 -->
    <el-dialog :visible.sync="pwdDialogVisible" title="修改密码" width="440px" append-to-body :close-on-click-modal="false">
      <el-form ref="pwdFormRef" :model="pwdForm" :rules="pwdRules" label-width="100px">
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" show-password placeholder="请输入旧密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" show-password placeholder="请输入新密码（至少3位）" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="pwdDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitChangePassword" :loading="pwdSubmitting">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<style scoped>
.main-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--bg-primary);
}

/* ============================================================
   第一行：系统头部（Logo + 标题 + 团队 + 用户）
   ============================================================ */
.top-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 68px;
  padding: 0 28px;
  flex-shrink: 0;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border-subtle);
  z-index: 20;
  position: relative;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-team {
  font-size: 0.88rem;
  color: var(--text-secondary);
  letter-spacing: 0.05em;
  white-space: nowrap;
  font-weight: 500;
}

.header-center {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
  flex: 1;
  justify-content: flex-end;
}

.header-logo {
  width: 36px; height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
}

.header-title {
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
}

.header-datetime {
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: var(--font-mono);
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.user-trigger {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  color: var(--text-secondary);
  padding: 5px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}
.user-trigger:hover { color: var(--accent-primary); }

/* ============================================================
   第二行：导航标签栏
   ============================================================ */
.top-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 28px;
  flex-shrink: 0;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border-subtle);
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  z-index: 19;
}

.nav-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 22px;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.88rem;
  position: relative;
  transition: all var(--transition-fast);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;
}

.nav-tab:hover {
  color: var(--accent-primary);
  background: var(--accent-primary-soft);
}

.nav-tab.active {
  color: var(--accent-primary);
  font-weight: 600;
  border-bottom-color: var(--accent-primary);
  background: transparent;
}

.nav-tab i { font-size: 1rem; }

/* ============================================================
   页面内容
   ============================================================ */
.page-content {
  flex: 1;
  overflow-y: auto;
  padding: 28px 0 36px;
}

.page-content > div {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 28px;
}

/* ============================================================
   响应式
   ============================================================ */
@media screen and (max-width: 900px) {
  .top-header { padding: 0 14px; }
  .header-team { font-size: 0.76rem; }
  .header-title { font-size: 1rem; }
  .header-datetime { display: none; }
  .top-nav { padding: 0 10px; height: 42px; }
  .nav-tab { padding: 6px 12px; font-size: 0.8rem; }
  .nav-tab span { display: none; }
  .page-content { padding: 16px; }
}

@media screen and (max-width: 540px) {
  .header-team { font-size: 0.7rem; }
  .header-logo { width: 28px; height: 28px; }
  .header-title { font-size: 0.85rem; }
  .nav-tab { padding: 6px 10px; }
  .page-content { padding: 10px; }
}
</style>

<script>
export default {
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.pwdForm.newPassword) { callback(new Error('两次输入不一致')); } else { callback(); }
    };
    const validateNewPwd = (rule, value, callback) => {
      if (value && value === this.pwdForm.oldPassword) { callback(new Error('新密码不能与原密码相同')); } else { callback(); }
    };
    return {
      dateTimeText: '',
      dateTimeTimer: null,
      userInfo: { userId: '', userName: '', role: '' },
      defaultName: '用户',
      roleLabel: '用户',
      teamLabel: '山东大学-行星科学团队',
      pwdDialogVisible: false, pwdSubmitting: false,
      pwdForm: { oldPassword: '', newPassword: '', confirmPassword: '' },
      pwdRules: {
        oldPassword: [{ required: true, message: '请输入旧密码', trigger: 'blur' }],
        newPassword: [{ required: true, min: 3, message: '新密码至少3位', trigger: 'blur' }, { validator: validateNewPwd, trigger: 'blur' }],
        confirmPassword: [{ required: true, message: '请确认新密码', trigger: 'blur' }, { validator: validateConfirm, trigger: 'blur' }]
      },
      menuItems: [
        { path: '/user/home', icon: 'el-icon-s-home', label: '首页' },
        { path: '/user/announcement', icon: 'el-icon-bell', label: '系统公告' },
        { path: '/user/vision', icon: 'el-icon-view', label: '计算机视觉分析' },
        { path: '/user/video', icon: 'el-icon-video-camera', label: '实验视频传输' }
      ]
    }
  },

  computed: {
    activeMenu() { return this.$route.path; }
  },

  created() {
    this.updateDateTime();
    this.dateTimeTimer = setInterval(() => { this.updateDateTime(); }, 1000);
    this.fetchUserInfo();
  },

  beforeDestroy() {
    if (this.dateTimeTimer) { clearInterval(this.dateTimeTimer); this.dateTimeTimer = null; }
  },

  methods: {
    updateDateTime() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const ss = String(now.getSeconds()).padStart(2, '0');
      const weekMap = ['日', '一', '二', '三', '四', '五', '六'];
      this.dateTimeText = `${y}年${m}月${d}日 ${hh}:${mm}:${ss} 星期${weekMap[now.getDay()]}`;
    },

    async fetchUserInfo() {
      try {
        const resp = await this.$axios.get('/user/info');
        if (resp.data.code === 0) { this.userInfo = resp.data.data; }
      } catch (e) { console.warn('获取用户信息失败:', e); }
    },

    handleCommand(command) {
      if (command === 'personal') {
        this.$router.push('/personal-center');
      } else if (command === 'changePassword') {
        this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' };
        this.pwdDialogVisible = true;
        this.$nextTick(() => { if (this.$refs.pwdFormRef) this.$refs.pwdFormRef.clearValidate(); });
      } else if (command === 'switchAccount') {
        this.$axios.post('/api/session/destroy').finally(() => {
          this.$router.push('/login');
          this.$message.success('已退出登录');
        });
      }
    },

    async submitChangePassword() {
      const valid = await this.$refs.pwdFormRef.validate().catch(() => false);
      if (!valid) return;
      this.pwdSubmitting = true;
      try {
        const resp = await this.$axios.put('/user/changePassword', this.pwdForm);
        if (resp.data.code === 0) {
          this.pwdDialogVisible = false;
          this.$message.success('密码修改成功，请重新登录');
          this.$axios.post('/api/session/destroy').finally(() => {
            this.$router.push('/login');
          });
        } else { this.$message.error(resp.data.msg || '修改失败'); }
      } catch (e) { this.$message.error('修改密码失败'); } finally { this.pwdSubmitting = false; }
    }
  }
}
</script>
